import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Alert from '~/components/Alert';
import SelectField from '~/components/forms/SelectField';
import ToggleButtonGroupField from '~/components/forms/ToggleButtonGroupField';
import { FormAction, FormBody } from '~/components/FormUtils';
import PageHeader from '~/components/PageHeader';
import Button from '~/components/ui/Button';
import { IconName } from '~/components/ui/Icon';
import Stack from '~/components/ui/Stack';
import useCurrentUser from '~/modules/user/useCurrentUser';
import useLanguages from '~/modules/user/useLanguages';
import useThemes from '~/modules/user/useThemes';
import { useUpdateProfileLite } from '~/modules/user/useUpdateProfile';

const schema = Joi.object({
  language: Joi.string().allow(''),
  theme: Joi.string().allow(''),
});

function Interface() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const themes = useThemes();
  const languages = useLanguages();
  const { mutateAsync: updateProfile, isError } = useUpdateProfileLite();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm({
    mode: 'onTouched',
    resolver: joiResolver(schema),
    values: {
      theme: user?.theme?.name || '',
      language: user?.language?.name || '',
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const onSubmit = handleSubmit((data) =>
    updateProfile({
      theme: data.theme || null,
      language: data.language || null,
    }),
  );

  return (
    <Stack col gap={6} className="flex-1">
      <PageHeader id="interface" />
      {isError && <Alert variant="error" description={t('interface.error')} />}
      <form onSubmit={onSubmit}>
        <FormBody>
          <SelectField
            control={control}
            name="language"
            label={t('interface.labels.selectLanguage')}
            options={(languages || []).map(({ name }) => ({ value: name, label: name }))}
            placeholder={t('interface.placeholders.selectLanguage')}
            className="w-full max-w-[400px]"
          />
          {!!themes && (
            <ToggleButtonGroupField
              control={control}
              name="theme"
              label={t('interface.labels.selectTheme')}
              options={[
                ...(themes || []).map(({ name }) => ({
                  value: name,
                  label: name,
                  icon: (name === 'Light' ? 'lightMode' : 'darkMode') as IconName,
                })),
                { value: '', label: t('interface.theme.auto'), icon: 'hdrAuto' as IconName },
              ]}
            />
          )}
        </FormBody>
        <FormAction>
          <Button type="submit" isPending={isSubmitting} isDisabled={!isValid}>
            {t('interface.submit')}
          </Button>
        </FormAction>
      </form>
    </Stack>
  );
}

export default Interface;
